// v6 finance module — vertical-neutral labels + a per-vertical override map. Folds in the
// hospitality-delivery-wording.md swaps so a single codebase renders the right copy per host.

const BASE = {
  deliveryPerson: 'Delivery person',
  deliveryPersonPlural: 'Delivery people',
  deliveryPayments: 'Delivery & agent payments',
  codPhrase: 'cash on delivery',
  legOutbound: 'Delivery',
  legReturn: 'Return',
  legVisit: 'Visit',
  remindOwner: 'Remind owner'
};

const OVERRIDES = {
  car_rental: {
    deliveryPerson: 'Car driver',
    deliveryPersonPlural: 'Car drivers',
    deliveryPayments: 'Driver payments',
    legOutbound: 'Handover',
    legReturn: 'Return'
  },
  hospitality: {
    deliveryPerson: 'Reservation manager',
    deliveryPersonPlural: 'Reservation managers',
    deliveryPayments: 'Reservation-manager payments',
    codPhrase: 'cash on arrival',
    legOutbound: 'Check-in',
    legReturn: 'Check-out'
  }
};

export function wordingFor(vertical) {
  return { ...BASE, ...(OVERRIDES[vertical] || {}) };
}

export function legLabel(vertical, leg) {
  const w = wordingFor(vertical);
  return { OUTBOUND: w.legOutbound, RETURN: w.legReturn, VISIT: w.legVisit }[leg] || leg;
}

export const VERTICALS = ['car_rental', 'hospitality', 'jewelry', 'real_estate', 'gym'];
export const VERTICAL_LABEL = {
  car_rental: 'Car rental',
  hospitality: 'Hospitality',
  jewelry: 'Jewellery',
  real_estate: 'Real estate',
  gym: 'Gym'
};

// ---- §A9: describe every settlement-config flag (label / unit / help / appliesTo) ----
// `kind`: 'money' | 'rate' (0–1) | 'int' | 'perVerticalHours' | 'tiers'.
export const SETTLEMENT_CONFIG_FIELDS = [
  {
    key: 'refundWindowHours',
    kind: 'perVerticalHours',
    label: 'Cancellation refund window',
    unit: 'hours',
    appliesTo: 'all',
    help: 'A cancellation made within this many hours of booking is fully refunded (minus non-refundable platform fees). After it, the forfeit schedule applies.'
  },
  {
    key: 'financeLeadApprovalThreshold',
    kind: 'money',
    label: 'Finance Lead approval threshold',
    unit: '₹',
    appliesTo: 'all',
    help: 'A Finance Lead can approve a settlement or refund up to this rupee amount on their own. Above it, a Finance Manager must approve.'
  },
  {
    key: 'cancellationMaxForfeitRate',
    kind: 'rate',
    label: 'Maximum forfeit rate',
    unit: 'fraction (0–1)',
    appliesTo: 'all',
    help: 'Hard ceiling on how much of the rental a customer can lose on cancellation, as a fraction (0.30 = 30%). Overrides any higher tier.'
  },
  {
    key: 'platformFeeRate',
    kind: 'rate',
    label: 'Platform fee rate',
    unit: 'fraction (0–1)',
    appliesTo: 'all',
    help: 'The customer-facing platform fee, as a fraction of the rental / membership amount (0.05 = 5%). This is the global default — set a per-business rate on an owner’s Overrides row to charge that business a different fee.'
  },
  {
    key: 'commissionRate',
    kind: 'rate',
    label: 'Commission rate',
    unit: 'fraction (0–1)',
    appliesTo: 'all',
    help: "The website's commission on each booking, deducted from the owner's payout (0.18 = 18%). This is the global default — set a per-business rate on an owner’s Overrides row to give that business different terms."
  },
  {
    key: 'depositRate',
    kind: 'rate',
    label: 'Security deposit rate',
    unit: 'fraction (0–1)',
    appliesTo: 'all',
    help: 'Refundable security deposit as a fraction of the rental amount (0.10 = 10%), charged only on listings whose owner opted in. Set to 0 to collect no deposit anywhere.'
  },
  {
    key: 'deliveryAssignmentSlaHours',
    kind: 'int',
    label: 'Delivery assignment SLA',
    unit: 'hours',
    appliesTo: 'car_rental, jewellery, hospitality',
    help: 'Hours an owner has to assign a delivery person after a booking is confirmed. After this the auto-assignment sweep steps in, and the booking shows in Pending items.'
  },
  {
    key: 'deliveryFeeShareRate',
    kind: 'rate',
    label: 'Delivery fee share',
    unit: 'fraction (0–1)',
    appliesTo: 'all',
    help: 'Share of each delivery charge that the delivery person earns per leg (0.7 = 70%).'
  },
  {
    key: 'deliveryFeeMinAmount',
    kind: 'money',
    label: 'Delivery fee minimum',
    unit: '₹',
    appliesTo: 'all',
    help: 'Floor payment per delivery leg — the delivery person earns at least this even if the share works out lower.'
  },
  {
    key: 'agentVisitFee',
    kind: 'money',
    label: 'Agent fee per site visit',
    unit: '₹',
    appliesTo: 'real estate',
    help: 'Flat amount a real-estate site-visit agent earns per completed visit (a visit carries no delivery charge).'
  },
  {
    key: 'interBusinessBaseFeePerLeg',
    kind: 'money',
    label: 'Inter-business base fee per leg',
    unit: '₹',
    appliesTo: 'car rental, jewellery',
    help: "What one business owes another for one leg when it borrows the other's shared delivery person."
  },
  {
    key: 'interBusinessFailedAttemptFee',
    kind: 'money',
    label: 'Inter-business failed-attempt fee',
    unit: '₹',
    appliesTo: 'car rental, jewellery',
    help: 'Charged to the borrowing business for each failed delivery attempt by a shared delivery person.'
  },
  {
    key: 'interBusinessWaitingRatePer15Min',
    kind: 'money',
    label: 'Inter-business waiting rate',
    unit: '₹ / 15 min',
    appliesTo: 'car rental, jewellery',
    help: 'Charged to the borrowing business per 15 minutes a shared delivery person waits at pickup or drop.'
  },
  {
    key: 'realEstateVisitFee',
    kind: 'money',
    label: 'Real-estate visit fee',
    unit: '₹ / confirmed visit',
    appliesTo: 'real estate',
    help: "Platform's fee to a real-estate owner per confirmed site visit (real estate takes no rent, so this is the platform's cut)."
  },
  {
    key: 'reconciliationAutoApproveAbs',
    kind: 'money',
    label: 'Billing-adjustment auto-approve',
    unit: '₹',
    appliesTo: 'all',
    help: 'A post-booking billing change whose total moves by at most this many rupees is applied automatically. Larger revenue-reducing changes go to the Billing adjustments queue.'
  },
  {
    key: 'settlementTimelineDays',
    kind: 'int',
    label: 'Settlement timeline',
    unit: 'days',
    appliesTo: 'all',
    help: 'Days after a payable is raised before finance may remind the owner or the payee may escalate. Drives the "overdue" flag and the Pending items page.'
  },
  {
    key: 'maxOwnerReminders',
    kind: 'int',
    label: 'Max owner reminders',
    unit: 'count (0 = no limit)',
    appliesTo: 'all',
    help: 'How many times finance may nudge an owner about one unpaid payable before only escalation remains. 0 = no limit.'
  },
  {
    key: 'forfeitScheduleTiers',
    kind: 'tiers',
    label: 'Forfeit schedule tiers',
    unit: '',
    appliesTo: 'all',
    help: 'Tiered cancellation forfeiture: for each row, a customer cancelling this many days before start forfeits this fraction of the rental. Capped by the max forfeit rate above.'
  }
];

// Owner-override fields (RentalUser.financeOverrides). "blank = platform default" is shown as
// helper text, not baked into the label.
export const OWNER_OVERRIDE_FIELDS = [
  { key: 'commissionRate', kind: 'rate', label: 'Commission rate', unit: 'fraction (0–1)', help: "The website's cut of each booking for this owner." },
  { key: 'platformFeeRate', kind: 'rate', label: 'Platform fee rate', unit: 'fraction (0–1)', help: 'Customer-facing platform fee rate applied to this owner’s bookings.' },
  { key: 'financeLeadApprovalThreshold', kind: 'money', label: 'Finance Lead approval threshold', unit: '₹', help: 'Overrides the platform threshold for settlements/refunds on this owner’s bookings.' },
  { key: 'cancellationMaxForfeitRate', kind: 'rate', label: 'Maximum forfeit rate', unit: 'fraction (0–1)', help: 'Overrides the platform forfeit ceiling for this owner.' }
];

export const OWNER_FINANCE_MODULES = [
  { key: 'delivery_payments', label: 'Delivery / driver / agent payments' },
  { key: 'b2b_billing', label: 'Shared-delivery (B2B) billing' },
  { key: 'visit_fees', label: 'Property visit fees' }
];
